import { bookingIdToDiseaseId } from 'constants/diseases'

import { useState } from 'react'
import T from 'prop-types'
import { useHistory } from 'react-router-dom'
import { get } from 'lodash'
import Joi from 'joi'
import { createVaccination } from 'lib/requests'
import Input from 'components/Input'
import Message from 'components/Message'
import useSession from 'hooks/useSession'
import { calculatePriority, decideNewPriority } from 'apps/booking/helpers/calculatePriority'

import Step from '../../components/Step'
import {
  getOptions as getClinicalInformationOptions,
  buildClinicalQuestionsValidator,
} from './ClinicalInformation'
import { getOptions as getWomanOptions } from './WomanQuestions'

export const validator = () =>
  Joi.object({
    person: Joi.object({
      phone: Joi.string().min(8).required(),
    })
      .required()
      .unknown(true),
  })
    .required()
    .unknown(true)

export default function ContactInformation({ changeStep, values, onChange, orgUnit, bookingType }) {
  const history = useHistory()
  const {
    session: { user_id, first_name, last_name },
  } = useSession()
  const nationalId = get(values, 'details.on_behalf_of.national_id', user_id)

  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (key) => ({ target: { value } }) => {
    onChange(key, value)
  }

  // Custom logic for "on behalf of" bookings
  const isBookingOnBehalfOf = Boolean(values?.details?.on_behalf_of?.national_id)

  // Submit user data
  async function handleSubmit() {
    // Decide which priority the user has based on previous answers (and age)
    const calculatedPriority = calculatePriority({
      nationalId,
      isHealthPersonnel: values?.rejection === 'helsepersonell',
      isRiskGroupA: values?.riskGroupA,
      isRiskGroupB: values?.riskGroupB,
    })

    // Decide which priority we're going to use. The calculated one or (if available) the one set by an admin
    let decidedPriority = decideNewPriority({
      calculatedPriority,
      originalPriority: values?.priority,
    })

    // Use "behalf of" persons priority (based on age only)
    if (isBookingOnBehalfOf) {
      decidedPriority = calculatePriority({
        nationalId: values?.details?.on_behalf_of?.national_id,
      })
    }

    // Save priority to session storage
    onChange('decidedPriority', decidedPriority)

    const diseaseId = bookingIdToDiseaseId(bookingType.id)

    // Prepare user data (including priority)
    const postData = {
      person: {
        ...(values?.person || {}),
        national_id: user_id,
        first_name,
        last_name,
      },
      vaccination: {
        ...(values?.vaccination || {}),
        org_unit: orgUnit.id,
        disease_id: diseaseId,
        vaccination_status: 'NOT_COMPLETED',
      },
      priority: decidedPriority,
    }

    // Check if any of the clinical questions disqualifies the user for a vaccine booking
    const mvpVaccinationValidator = buildClinicalQuestionsValidator(
      [...getWomanOptions(values, user_id), ...getClinicalInformationOptions(values, user_id)],
      true,
    )
    const isClinicalErrors = !!mvpVaccinationValidator.validate(values)?.error

    // Covid only
    // Send user to decline page if clinical errors
    const isCovidVaccine = bookingType.id === 'COVID_VACCINE'
    if (isCovidVaccine && isClinicalErrors) {
      // Skip saving for "on behalf of" bookings
      if (!isBookingOnBehalfOf) {
        setIsSubmitting(true)
        // Create a vaccination (without a booking)
        const response = await createVaccination({ data: postData, ouId: orgUnit.id })
        setIsSubmitting(false)
        // Handle errors
        if (response.error) {
          return setError(get(response, 'error.message', 'Noe gikk feil'))
        }
      }

      history.push(`/${orgUnit.slug}/avslag-klinisk`)
      return
    }

    // Send user to booking if eligible for a vaccine or it's a "on behalf of" booking
    const isValid = values?.isEligible || isBookingOnBehalfOf

    // Special rules for flu - use priority group calculated in frontend and compare with allowed priority groups from backend
    const isFluVaccine = bookingType.id === 'FLU_VACCINE'
    const isValidFlu = isFluVaccine && values?.eligiblePriorityGroups?.includes(decidedPriority)

    if (isValid || isValidFlu) {
      // Go to next page for booking
      changeStep()
      return
    }

    // User who should not have a booking yet...
    // Create a vaccination (without a booking)
    setIsSubmitting(true)

    // This if is not needed, since we return isValid for "on behalf bookings" above
    // Keeping it here as a safeguard in case we rearrange something in this messy logic later
    if (!isBookingOnBehalfOf) {
      const response = await createVaccination({ data: postData, ouId: orgUnit.id })
      setIsSubmitting(false)
      // Handle errors
      if (response.error) {
        return setError(get(response, 'error.message', 'Noe gikk feil'))
      }

      // Send user to a receipt page
      if (isFluVaccine) {
        history.push(`/${orgUnit.slug}/avslag-influensa`)
      } else {
        history.push(`/${orgUnit.slug}/venteliste`)
      }
    }
  }

  return (
    <Step
      title="Hva er ditt mobilnummer?"
      onSubmit={handleSubmit}
      submit="Gå videre"
      submitDisabled={!!validator(values).validate(values)?.error || isSubmitting}
      isLoading={isSubmitting}
      info={values?.isEligible ? 'Bekreftelse på bestilt time vil sendes til dette nummeret.' : ''}
      footerSpacing
    >
      <Input
        short
        label="Telefonnummer"
        type="tel"
        tooltip="Norske telefonnummer kan legges inn uten landkode. Ved utenlandsk telefonnummer må man legge til landets retningsnummer først. Svenske nummer starter for eksempel med +46."
        value={get(values, 'person.phone', '')}
        onChange={handleChange('person.phone')}
      />

      {error && (
        <Message type="warning" focus>
          {error}
        </Message>
      )}
    </Step>
  )
}

ContactInformation.propTypes = {
  changeStep: T.func.isRequired,
  values: T.object.isRequired,
  onChange: T.func.isRequired,
  orgUnit: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
  }).isRequired,
  bookingType: T.shape({
    id: T.string.isRequired,
    slug: T.string.isRequired,
    title: T.string.isRequired,
  }).isRequired,
}
